import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import cx from 'classnames';

import { locale, scrollToElement, setInLocalStorage } from '@trqk/utils';

import { TourCard } from '../../TourCard';
import { HASHES, PATHS } from '../../../../../../constants';
import {
    DETECTIONS_BY_PRODUCTION_COMPANY_WRAPPER_ID,
    TOP_PUBLISHERS_WRAPPER_ID,
} from '../../../constants';

import iconUrl from '../../../../../../assets/svg/insights-empty-breakdown.svg';
import grayIconUrl from '../../../../../../assets/svg/insights-empty-breakdown-grayscale.svg';

/**
 * Displays the Track Your Placements step of the Insights cards tour.
 */
export const TrackPlacements = ({
    checked,
    localStorageKey,
    step,
}: TourCardInstanceProps): JSX.Element => {
    // Other tour cards don't need to manage local state because when the user clicks a link, it
    // takes them to another page. TrackPlacements needs to enter the 'checked' state when a user
    // scrolls to the TopPublishersCard or the DetectionsByProductionSummaryCard, so that means
    // it has to track the checked state locally. We initialize the checked state from the passed-
    // in value, which is originally read from localStorage when the Insights page mounts.
    const [localChecked, setLocalChecked] = useState(checked);

    return (
        <TourCard
            body={
                <div className="text-center">
                    {
                        locale.enUS.INSIGHTS.CARDS.STEP_4_TRACK_PLACEMENTS.BODY
                            .PART_1
                    }{' '}
                    <Link
                        className={cx('underline', {
                            'text-primary-500': !localChecked,
                            'text-tertiary-400': localChecked,
                        })}
                        to={({ search }) => ({
                            hash: HASHES.STATEMENTS.BREAKDOWN,
                            pathname: `${PATHS.STATEMENTS}/charts`,
                            search,
                            state: {
                                fromLink: true,
                            },
                        })}
                        onClick={(): void => {
                            if (localStorageKey) {
                                setInLocalStorage(
                                    localStorageKey,
                                    (): boolean => true,
                                );
                            }
                        }}
                    >
                        {
                            locale.enUS.INSIGHTS.CARDS.STEP_4_TRACK_PLACEMENTS
                                .BODY.PART_2
                        }
                    </Link>
                    {', '}
                    <button
                        className={cx('underline font-medium', {
                            'text-primary-500': !localChecked,
                            'text-tertiary-400': localChecked,
                        })}
                        type="button"
                        onClick={() => {
                            setLocalChecked(true);

                            scrollToElement(TOP_PUBLISHERS_WRAPPER_ID).then(
                                (didScroll: boolean) => {
                                    if (didScroll && localStorageKey) {
                                        setInLocalStorage(
                                            localStorageKey,
                                            (): boolean => true,
                                        );
                                    }
                                },
                            );
                        }}
                    >
                        {
                            locale.enUS.INSIGHTS.CARDS.STEP_4_TRACK_PLACEMENTS
                                .BODY.PART_3
                        }
                    </button>{' '}
                    {
                        locale.enUS.INSIGHTS.CARDS.STEP_4_TRACK_PLACEMENTS.BODY
                            .AND
                    }{' '}
                    <button
                        className={cx('underline font-medium', {
                            'text-primary-500': !localChecked,
                            'text-tertiary-400': localChecked,
                        })}
                        type="button"
                        onClick={() => {
                            setLocalChecked(true);

                            scrollToElement(
                                DETECTIONS_BY_PRODUCTION_COMPANY_WRAPPER_ID,
                            ).then((didScroll: boolean) => {
                                if (didScroll && localStorageKey) {
                                    setInLocalStorage(
                                        localStorageKey,
                                        (): boolean => true,
                                    );
                                }
                            });
                        }}
                    >
                        {
                            locale.enUS.INSIGHTS.CARDS.STEP_4_TRACK_PLACEMENTS
                                .BODY.PART_4
                        }
                    </button>
                    .
                </div>
            }
            checked={localChecked}
            grayIconUrl={grayIconUrl}
            iconUrl={iconUrl}
            iconWidth={120}
            step={step}
            title={locale.enUS.INSIGHTS.CARDS.STEP_4_TRACK_PLACEMENTS.TITLE}
        />
    );
};
